import { useState } from "react";
import { HiOutlineXMark, HiOutlinePaperAirplane, HiOutlinePhoto, HiOutlineLink, HiOutlineDocumentText, HiOutlineEnvelope, HiOutlineCheckCircle, HiOutlineExclamationCircle } from "react-icons/hi2";
import "./BroadcastModal.css";
import toast from "react-hot-toast";

/**
 * BroadcastModal
 * @param {string}   recipientType  "subscribers" | "applicants"
 * @param {number[]} selectedIds    IDs of selected rows (empty = send to all)
 * @param {number}   totalCount     Total in the list (for display)
 * @param {Function} onSend         async (payload) => { sent, failed, total }
 * @param {Function} onClose
 */
export default function BroadcastModal({ recipientType, selectedIds = [], totalCount = 0, onSend, onClose }) {
  const [subject, setSubject] = useState("");
  const [heading, setHeading] = useState("");
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [ctaText, setCtaText] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { sent, failed, total, message }
  const [error, setError] = useState("");
  const [tab, setTab] = useState("compose"); // compose | preview

  const recipientLabel = recipientType === "subscribers" ? "subscriber" : "applicant";
  const recipientCount = selectedIds.length > 0 ? selectedIds.length : totalCount;

  const preview = {
    heading: heading || "Your message heading",
    body: body || "Your message body will appear here...",
    imageUrl,
    ctaText,
    ctaUrl,
  };

  const handleSend = async () => {
    if (!subject.trim()) {
      setError("Subject is required.");
      return;
    }
    if (!heading.trim()) {
      setError("Heading is required.");
      return;
    }
    if (!body.trim()) {
      setError("Body message is required.");
      return;
    }
    setError("");
    setSending(true);
    try {
      const payload = {
        subject,
        heading,
        body,
        ...(imageUrl ? { imageUrl } : {}),
        ...(ctaText && ctaUrl ? { ctaText, ctaUrl } : {}),
        ...(selectedIds.length > 0 ? { recipientIds: selectedIds } : {}),
      };
      const res = await onSend(payload);
      setResult({ ...res.data, message: res.message });
      toast.success(res.message || "Emails sent successfully!");
    } catch (err) {
      const errMsg = err.message || "Failed to send. Check SMTP settings.";
      setError(errMsg);
      toast.error(errMsg, { duration: 7000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="bm__overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bm__modal">
        {/* Header */}
        <div className="bm__head">
          <div className="bm__head-left">
            <HiOutlineEnvelope className="bm__head-icon" />
            <div>
              <h2 className="bm__title">Compose Email</h2>
              <p className="bm__sub">
                Sending to{" "}
                <strong>
                  {selectedIds.length > 0
                    ? `${selectedIds.length} selected ${recipientLabel}${selectedIds.length !== 1 ? "s" : ""}`
                    : `all ${totalCount} ${recipientLabel}${totalCount !== 1 ? "s" : ""}`}
                </strong>
              </p>
            </div>
          </div>
          <button type="button" className="bm__close" onClick={onClose}>
            <HiOutlineXMark />
          </button>
        </div>

        {/* If sent — show result */}
        {result ? (
          <div className="bm__result">
            <HiOutlineCheckCircle className="bm__result-icon bm__result-icon--ok" />
            <h3 className="bm__result-title">{result.message || "Emails sent!"}</h3>
            <div className="bm__result-stats">
              <div className="bm__result-stat">
                <span>{result.sent}</span>
                <p>Sent</p>
              </div>
              <div className="bm__result-stat">
                <span>{result.failed || 0}</span>
                <p>Failed</p>
              </div>
              <div className="bm__result-stat">
                <span>{result.total}</span>
                <p>Total</p>
              </div>
            </div>
            <button type="button" className="a-btn a-btn--outline" style={{ marginTop: "1.25rem" }} onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="bm__tabs">
              {["compose", "preview"].map((t) => (
                <button key={t} type="button" className={`bm__tab ${tab === t ? "bm__tab--active" : ""}`} onClick={() => setTab(t)}>
                  {t === "compose" ? "✏️ Compose" : "👁 Preview"}
                </button>
              ))}
            </div>

            {tab === "compose" ? (
              <div className="bm__body">
                {/* Subject */}
                <div className="bm__field">
                  <label className="bm__label">
                    Subject Line <span className="bm__req">*</span>
                  </label>
                  <input type="text" className="bm__input" placeholder="e.g. Important update from Forge Retreat" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} />
                  <p className="bm__char">{subject.length}/200</p>
                </div>

                {/* Heading */}
                <div className="bm__field">
                  <label className="bm__label">
                    Email Heading <span className="bm__req">*</span>
                  </label>
                  <input type="text" className="bm__input" placeholder="Big bold headline in the email" value={heading} onChange={(e) => setHeading(e.target.value)} maxLength={150} />
                </div>

                {/* Body */}
                <div className="bm__field">
                  <label className="bm__label">
                    Message Body <span className="bm__req">*</span>
                  </label>
                  <textarea
                    className="bm__textarea"
                    rows={7}
                    placeholder="Write your message here. Plain text or basic HTML is supported. Use a new line for paragraphs."
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                  />
                </div>

                {/* Optional: Image URL */}
                <div className="bm__optional-section">
                  <p className="bm__optional-title">Optional Extras</p>
                  <div className="bm__field">
                    <label className="bm__label">
                      <HiOutlinePhoto /> Header Image URL
                    </label>
                    <input type="url" className="bm__input" placeholder="https://example.com/image.jpg (leave blank for no image)" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} />
                    <p className="bm__hint">Recommended: 1200×400px. Hosted image URL (Cloudinary, S3, etc.)</p>
                  </div>

                  {/* CTA */}
                  <div className="bm__field-row">
                    <div className="bm__field" style={{ flex: 1 }}>
                      <label className="bm__label">
                        <HiOutlineLink /> Button Text
                      </label>
                      <input type="text" className="bm__input" placeholder="e.g. Learn More" value={ctaText} onChange={(e) => setCtaText(e.target.value)} />
                    </div>
                    <div className="bm__field" style={{ flex: 2 }}>
                      <label className="bm__label">
                        <HiOutlineLink /> Button URL
                      </label>
                      <input type="url" className="bm__input" placeholder="https://forge.ngo/..." value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} />
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="bm__error">
                    <HiOutlineExclamationCircle /> {error}
                  </div>
                )}
              </div>
            ) : (
              /* ── Preview ── */
              <div className="bm__preview">
                <p className="bm__preview-label">Email Preview</p>
                <div className="bm__preview-email">
                  {/* Mini email render */}
                  <div className="bm__prev-header">
                    <span className="bm__prev-brand">FORGE RETREAT COLOMBIA</span>
                  </div>
                  {preview.imageUrl && <img src={preview.imageUrl} alt="header" className="bm__prev-img" onError={(e) => (e.target.style.display = "none")} />}
                  <div className="bm__prev-body">
                    <h2 className="bm__prev-heading">{preview.heading}</h2>
                    <div className="bm__prev-text" dangerouslySetInnerHTML={{ __html: preview.body.replace(/\n/g, "<br/>") }} />
                    {preview.ctaText && preview.ctaUrl && (
                      <a href={preview.ctaUrl} className="bm__prev-cta" target="_blank" rel="noreferrer">
                        {preview.ctaText} →
                      </a>
                    )}
                  </div>
                  <div className="bm__prev-footer">
                    <p>© {new Date().getFullYear()} Forge Retreat Colombia · forge.ngo</p>
                    <p>You're receiving this because you're part of the Forge community.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="bm__footer">
              <div className="bm__footer-info">
                <HiOutlineDocumentText />
                <span>
                  {subject ? `"${subject.slice(0, 40)}${subject.length > 40 ? "…" : ""}"` : "No subject yet"}
                  {" · "}
                  {recipientCount} recipient{recipientCount !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="bm__footer-actions">
                <button type="button" className="a-btn a-btn--outline" onClick={onClose} disabled={sending}>
                  Cancel
                </button>
                <button type="button" className="a-btn a-btn--rust" onClick={handleSend} disabled={sending}>
                  <HiOutlinePaperAirplane />
                  {sending ? "Sending…" : `Send to ${recipientCount} Recipient${recipientCount !== 1 ? "s" : ""}`}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
